import { Alert, Button, Form, Popconfirm, Table, Tag, message } from "antd";
import { CloseCircleFilled } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import CustomCheckBox from "../../../../../components/checkbox/CustomCheckBox";
import { serverUrl } from "../../../../../nestserver";
import DayPermissionTag from "../../../../../components/dayPermission/DayPermissionTag";

// Rows come back either as a bare array or wrapped in { data, classDetails,
// holiday } depending on the endpoint's shape.
const readRecords = (attendanceData) => {
  const result = attendanceData?.data?.result;
  if (!result) return [];
  return Array.isArray(result) ? result : result.data || [];
};

// A student is "Not marked" until a record exists for them — the server sends
// _id: null for students it had to synthesise. That distinction is what keeps
// "absent" meaningful for the absence alerts and the analytics dashboards.
const deriveStatus = (state, isHoliday) => {
  if (isHoliday) return { label: "Holiday", color: "blue" };
  if (state?.permission?.hasPermission) {
    return { label: state.permission.permissionType, color: "green" };
  }
  // A session excused by an approved Day Permission (late arrival / early
  // pickup / flex) is not an absence.
  const morningExcused = !!state?.permission?.morningPermission && !state.check_in;
  const afternoonExcused =
    !!state?.permission?.afternoonPermission && !state.check_out;
  if (morningExcused || afternoonExcused) {
    const otherOk =
      (morningExcused || state.check_in) && (afternoonExcused || state.check_out);
    if (morningExcused && afternoonExcused) {
      return { label: "Permission", color: "blue" };
    }
    if (otherOk) return { label: "Half Day (Permission)", color: "orange" };
    if (state.exists) return { label: "Absent", color: "red" };
  }
  if (!state?.exists) return { label: "Not marked", color: "default" };
  if (state.check_in && state.check_out) {
    return { label: "Present", color: "green" };
  }
  if (state.check_in || state.check_out) {
    return { label: "Partial", color: "gold" };
  }
  return { label: "Absent", color: "red" };
};

const MontessoriAttendanceTable = ({
  attendanceData,
  classId,
  selectedDate,
  isAdmin,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [attendanceState, setAttendanceState] = useState({});
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isClosingRegister, setIsClosingRegister] = useState(false);
  // Keyed "<studentId>:<field>" while that one cell is being saved.
  const [savingCells, setSavingCells] = useState({});
  // Counts in-flight saves so a refetch landing mid-save can't clobber them.
  const savingCountRef = useRef(0);

  const records = readRecords(attendanceData);
  const classDetails = attendanceData?.data?.result?.classDetails;

  useEffect(() => {
    if (savingCountRef.current > 0) return;

    const rows = readRecords(attendanceData);
    const initialState = {};
    rows.forEach((record) => {
      const studentId = record?.student?._id;
      if (!studentId) return;

      initialState[studentId] = {
        _id: record._id || null,
        exists: !!record._id,
        check_in: !!record.is_checked_in,
        check_out: !!record.is_checked_out,
        check_in_time: record.check_in_time || null,
        check_out_time: record.check_out_time || null,
        permission: record?.permission,
        student_name: `${record?.student?.first_name} ${record?.student?.last_name}`,
      };
    });

    setAttendanceState(initialState);
    // dataUpdatedAt only changes when new server data actually lands, unlike
    // the query result object, whose identity changes on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceData?.dataUpdatedAt, classId, selectedDate]);

  /**
   * Save one checkbox for one student. Applies optimistically, reconciles with
   * the row the server echoes back, and rolls back on failure.
   *
   * Deliberately does NOT invalidate the react-query cache: the response is the
   * full row, so local state is server state once it resolves. Invalidating
   * would fire a full-class GET per tick and race the next optimistic update,
   * and nothing class-level changes when a single student is marked.
   */
  const persistFields = async (
    studentId,
    patch,
    { cell, optimistic = patch } = {},
  ) => {
    const cellKey = `${studentId}:${cell}`;
    if (savingCells[cellKey]) return;

    const previous = attendanceState[studentId];
    savingCountRef.current += 1;
    setSavingCells((prev) => ({ ...prev, [cellKey]: true }));
    setAttendanceState((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], ...optimistic },
    }));

    try {
      // Only the fields being changed are sent, so two staff marking the same
      // child cannot overwrite each other's other box.
      const payload = { date: selectedDate };
      if ("check_in" in patch) payload.is_checked_in = patch.check_in;
      if ("check_out" in patch) payload.is_checked_out = patch.check_out;

      const res = await axios.patch(
        `${serverUrl}/montessori/attendance/class/${classId}/student/${studentId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );

      const saved = res?.data?.result?.data;
      setAttendanceState((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          _id: saved?._id ?? prev[studentId]?._id,
          exists: true,
          check_in: !!saved?.is_checked_in,
          check_out: !!saved?.is_checked_out,
          check_in_time: saved?.check_in_time ?? null,
          check_out_time: saved?.check_out_time ?? null,
        },
      }));
      return true;
    } catch (err) {
      console.error(err);
      setAttendanceState((prev) => ({ ...prev, [studentId]: previous }));
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Failed to save attendance",
      );
      return false;
    } finally {
      savingCountRef.current -= 1;
      setSavingCells((prev) => {
        const next = { ...prev };
        delete next[cellKey];
        return next;
      });
    }
  };

  const persistCell = (studentId, field, value) =>
    persistFields(studentId, { [field]: value }, { cell: field });

  /**
   * Record a child as absent in one tap, without the tick-then-untick
   * workaround. That workaround sent the parent a false "checked in" push on
   * the tick and nothing on the untick. This writes the absent record directly
   * and sends no check-in push.
   *
   * The server then sends the parent an absence alert about 40 seconds later
   * (held for 30 s so a mis-tap can still be undone), and the SMS follows at
   * the 1 PM sweep. Only today is announced, so the message says so.
   */
  const markAbsent = async (studentId) => {
    const saved = await persistFields(
      studentId,
      { check_in: false, check_out: false },
      {
        cell: "absent",
        optimistic: {
          check_in: false,
          check_out: false,
          check_in_time: null,
          check_out_time: null,
          exists: true,
        },
      },
    );
    if (saved) {
      message.success(
        isToday
          ? "Marked absent. The parent will be notified shortly."
          : "Marked absent. No alert is sent for a past date.",
      );
    }
  };

  /**
   * The bulk buttons FILL GAPS — they never overwrite a mark the teacher
   * already made. An overwriting "mark the whole class present" would silently
   * turn a recorded absence back into a present with a fabricated arrival time,
   * and the teacher would get no warning.
   *
   * Check-in targets children with no record at all. Check-out targets children
   * who are checked in but not yet out — completing their day rather than
   * deciding anything. Students on approved leave are never touched.
   */
  const remainingCheckIn = Object.entries(attendanceState).filter(
    ([, s]) =>
      !s.exists &&
      !s.permission?.hasPermission &&
      !s.permission?.morningPermission,
  );
  const remainingCheckOut = Object.entries(attendanceState).filter(
    ([, s]) =>
      s.check_in &&
      !s.check_out &&
      !s.permission?.hasPermission &&
      !s.permission?.afternoonPermission,
  );

  const isHoliday = !!attendanceData?.data?.result?.holiday?.isHoliday;
  const holidayName = attendanceData?.data?.result?.holiday?.name;
  // Students with no record at all — the ones Close Register would write.
  const unmarkedCount = remainingCheckIn.length;
  const isToday = selectedDate === moment().format("YYYY-MM-DD");

  const handleCheckIn = async () => {
    setIsCheckingIn(true);
    try {
      await axios.post(
        `${serverUrl}/montessori/attendance/check-in/class/${classId}`,
        {
          date: selectedDate,
          student_attendance: remainingCheckIn.map(([studentId]) => ({
            student_id: studentId,
            is_checked_in: true,
          })),
        },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );

      await queryClient.invalidateQueries([
        "montessori-attendance",
        classId,
        selectedDate,
      ]);

      message.success("Class Checked In successfully");
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Failed to check in",
      );
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleCheckOut = async () => {
    setIsCheckingOut(true);
    try {
      await axios.post(
        `${serverUrl}/montessori/attendance/check-out/class/${classId}`,
        {
          date: selectedDate,
          student_attendance: remainingCheckOut.map(([studentId]) => ({
            student_id: studentId,
            is_checked_out: true,
          })),
        },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );

      await queryClient.invalidateQueries([
        "montessori-attendance",
        classId,
        selectedDate,
      ]);

      message.success("Class Checked Out successfully");
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Failed to check out",
      );
    } finally {
      setIsCheckingOut(false);
    }
  };

  /**
   * Close the register: every student still without a record is written as
   * absent. Until this runs they have no document at all, which makes them
   * invisible to the absence alerts, the dashboards and the Excel export —
   * indistinguishable from a child nobody has got to yet.
   *
   * This one DOES invalidate the query: it writes across the whole class and
   * stamps the class record, so local state is no longer the whole picture.
   */
  const handleCloseRegister = async () => {
    setIsClosingRegister(true);
    try {
      const res = await axios.post(
        `${serverUrl}/montessori/attendance/class/${classId}/close-register`,
        { date: selectedDate },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );

      await queryClient.invalidateQueries([
        "montessori-attendance",
        classId,
        selectedDate,
      ]);

      const markedAbsent = res?.data?.result?.data?.markedAbsent ?? 0;
      message.success(
        markedAbsent === 0
          ? "Register closed. Every student was already marked."
          : `Register closed. ${markedAbsent} student${
              markedAbsent === 1 ? "" : "s"
            } marked absent.`,
      );
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Failed to close register",
      );
    } finally {
      setIsClosingRegister(false);
    }
  };

  // Reserve the line whether or not there is a time, so ticking a box doesn't
  // shift the row.
  const renderTime = (value) => (
    <span
      className="text-xs text-gray-500 leading-none"
      style={{ minHeight: 14 }}
    >
      {value ? moment(value).format("LT") : ""}
    </span>
  );

  const renderCheckCell = (record, field, timeField) => {
    const studentId = record?.student?._id;
    const studentState = attendanceState[studentId];

    if (isHoliday) {
      return (
        <div className="flex items-center justify-center">
          <span className="text-gray-400" title="Holiday">-</span>
        </div>
      );
    }

    if (studentState?.permission?.hasPermission) {
      return (
        <div className="flex items-center justify-center">
          <CloseCircleFilled style={{ color: "#f5222d", fontSize: "20px" }} />
        </div>
      );
    }

    // An untouched student and one deliberately marked absent both show an
    // empty box. Fading the untouched one keeps "nobody has decided yet"
    // visually distinct from "decided: absent".
    const undecided = !studentState?.exists;

    // A child cannot leave before arriving. Without this the register can reach
    // checked-out-but-never-checked-in, which reads as "Partial" — the label
    // for "arrived, hasn't left yet" — and is plainly wrong.
    const blockedUntilCheckIn =
      field === "check_out" && !studentState?.check_in;

    const tooltip = blockedUntilCheckIn
      ? "Check the student in first"
      : undecided
        ? "Not marked yet"
        : undefined;

    return (
      <div
        className="flex flex-col items-center justify-center gap-1"
        style={undecided || blockedUntilCheckIn ? { opacity: 0.4 } : undefined}
        title={tooltip}
      >
        <CustomCheckBox
          checked={!!studentState?.[field]}
          onChange={(e) => persistCell(studentId, field, e.target.checked)}
          disabled={
            !!savingCells[`${studentId}:${field}`] ||
            !!savingCells[`${studentId}:absent`] ||
            blockedUntilCheckIn
          }
        />
        {renderTime(studentState?.[timeField])}
        <DayPermissionTag
          info={
            field === "check_in"
              ? studentState?.permission?.morningPermission
              : studentState?.permission?.afternoonPermission
          }
        />
      </div>
    );
  };

  const columns = [
    {
      title: "Name",
      key: "name",
      align: "center",
      render: (record) => (
        <div
          style={{ color: "#111111" }}
          className="text-base font-semibold text-center capitalize"
        >
          {`${record?.student?.first_name} ${record?.student?.last_name}`}
        </div>
      ),
    },
    {
      title: "Check In",
      key: "check_in",
      align: "center",
      render: (record) => renderCheckCell(record, "check_in", "check_in_time"),
    },
    {
      title: "Check Out",
      key: "check_out",
      align: "center",
      render: (record) =>
        renderCheckCell(record, "check_out", "check_out_time"),
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      render: (record) => {
        const studentId = record?.student?._id;
        const state = attendanceState[studentId];
        const { label, color } = deriveStatus(state, isHoliday);
        // Offered only where nobody has decided yet. A child with a record is
        // already absent, present or on leave, and a holiday takes no marks.
        const canMarkAbsent =
          !!state &&
          !state.exists &&
          !state.permission?.hasPermission &&
          !isHoliday;
        return (
          <div className="flex items-center justify-center gap-2">
            <Tag color={color} style={{ margin: 0 }}>
              {label}
            </Tag>
            {canMarkAbsent && (
              <Button
                type="link"
                size="small"
                disabled={
                  !!savingCells[`${studentId}:absent`] ||
                  !!savingCells[`${studentId}:check_in`]
                }
                onClick={() => markAbsent(studentId)}
              >
                Mark absent
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="mt-8">
      <div className="mb-4 flex justify-between px-4">
        {classDetails?.check_in_time && (
          <Tag color="blue">
            Checked In: {moment(classDetails.check_in_time).format("LT")}
          </Tag>
        )}
        {classDetails?.check_out_time && (
          <Tag color="orange">
            Checked Out: {moment(classDetails.check_out_time).format("LT")}
          </Tag>
        )}
        {classDetails?.closed_at && (
          <Tag color="default">
            Register closed: {moment(classDetails.closed_at).format("LT")}
          </Tag>
        )}
      </div>

      {/* Nothing else prompts a teacher to close the register, and a child who
          is never marked has no record — so they are missed by the absence
          alert, the dashboards and the export entirely. */}
      {unmarkedCount > 0 && !isHoliday && (
        <Alert
          className="mb-4"
          type={isToday ? "warning" : "error"}
          showIcon
          message={`${unmarkedCount} student${
            unmarkedCount === 1 ? " has" : "s have"
          } no attendance recorded`}
          description={
            isToday
              ? "Close the register to record them as absent. Parents get an app alert straight away, and an SMS from 1:00 PM."
              : "These students were never marked for this day. Close the register to record them as absent. No alert is sent for a past date."
          }
        />
      )}

      <Form form={form}>
        <Table
          columns={columns}
          dataSource={records}
          loading={attendanceData?.isLoading}
          pagination={false}
          rowKey={(record) => record?.student?._id}
          footer={() => {
            return (
              <div className="flex justify-center pt-4 pb-2 gap-4">
                {/* Gated on the live per-row state, not on the query cache —
                    per-student saves don't invalidate it, so a cache-derived
                    gate goes stale the moment a teacher marks anyone. */}
                {!isAdmin && remainingCheckIn.length > 0 && !isHoliday && (
                  <Button
                    size="large"
                    type="primary"
                    className="text-white bg-green-600 hover:bg-green-500"
                    loading={isCheckingIn}
                    onClick={handleCheckIn}
                  >
                    Check In Remaining ({remainingCheckIn.length})
                  </Button>
                )}
                {!isAdmin && remainingCheckOut.length > 0 && !isHoliday && (
                  <Button
                    size="large"
                    type="primary"
                    className="text-white bg-red-600 hover:bg-red-500"
                    loading={isCheckingOut}
                    onClick={handleCheckOut}
                  >
                    Check Out Remaining ({remainingCheckOut.length})
                  </Button>
                )}
                {/* Records the children nobody marked as absent, which is what
                    gets them into the absence alerts and the export. */}
                {!isHoliday && unmarkedCount > 0 && (
                  <Popconfirm
                    title="Close the register?"
                    description={`${unmarkedCount} unmarked student${
                      unmarkedCount === 1 ? "" : "s"
                    } will be recorded as absent${
                      isToday
                        ? " and their parents notified."
                        : ". No alert is sent for a past date."
                    }`}
                    okText="Close register"
                    cancelText="Cancel"
                    onConfirm={handleCloseRegister}
                  >
                    <Button size="large" loading={isClosingRegister}>
                      Close Register ({unmarkedCount} unmarked)
                    </Button>
                  </Popconfirm>
                )}
                {isHoliday && (
                  <Tag color="red" className="text-lg p-2">
                    Holiday: {holidayName}
                  </Tag>
                )}
              </div>
            );
          }}
        />
      </Form>
    </div>
  );
};

export default MontessoriAttendanceTable;
